import React, { useState, useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import cx from "classnames";
import ClickOutsideHolder from "../ClickOutsideHolder";
import ErrorBlock from "../ErrorBlock";
import { withTranslation } from "react-i18next";
import ReactSVG from "react-svg";
import Loader from "../Loader";
import useDebounce from "../../../../custom-hooks/useDebounce";

import { openAddressModal, closeAddressModal } from "actions/ui";
// import AddressModal from "../../AddressModal";

import "./styles.scss";

const STATIC_BLOCK_FORM_KEY = "cust_address_widget_modal_form";

const mapDispatchToProps = { openAddressModal, closeAddressModal };

const Address = props => {
    const {
        t,
        meta,
        input,
        placeholder,
        disabled,
        label,
        required,
        isLoading,
        options: initOptions = [],
        withSearch,
        submitComboboxMulty,
        submitOnBlur,
        onOpen,
        isAsync,
        onInputChange,
        initialValues,
        customerId,
        customerObjectType,
        onModalOpen,
        openAddressModal,
        closeAddressModal,
    } = props;

    const [showOptions, setShowOptions] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    // const [isModalOpen, setIsModalOpen] = useState(false);
    const [createdOptions, setCreatedOptions] = useState([]);

    const searchInputRef = useRef(null);

    const { debouncedValue: debouncedQuery, setDebouncedValue: setDebouncedQuery } = useDebounce(searchQuery, 300);

    const existingOptions = useMemo(() => {
        if (isAsync) {
            // merge together initialValues with current values and get unique options (without duplicates)
            const initKeys = initialValues && Array.isArray(initialValues) ? initialValues.map(item => item.value) : [];
            const inputKeys =
                input && input.value && Array.isArray(input.value) ? input.value.map(item => item.value) : [];
            const initOptionsKeys =
                initOptions && Array.isArray(initOptions) ? initOptions.map(option => option.value) : [];
            const uniqueKeys = [...new Set([...initKeys, ...inputKeys, ...initOptionsKeys])];
            const mergedOptions = [...(input.value || []), ...(initOptions || []), ...(initialValues || [])];
            const options = uniqueKeys.map(key => mergedOptions.find(option => option.value === key));
            return options;
            // return initOptions.length > 0 ? initOptions : input.value;
        }
        if (debouncedQuery) {
            const query = debouncedQuery.toLowerCase();
            // get selected options (to keep them once options are being filtered)
            const selectedOptions =
                input && input.value && Array.isArray(input.value)
                    ? input.value.map(val => initOptions.find(option => option.value === val))
                    : [];

            // make sure to not include selected options in filtered options to prevent duplicate options
            const filteredOptions = initOptions.filter(option => {
                const isMatchQuery = option.label.toLowerCase().includes(query);
                const isSelected =
                    input && input.value && Array.isArray(input.value) ? input.value.includes(option.value) : false;
                return isMatchQuery && !isSelected;
            });
            return [...selectedOptions, ...filteredOptions];
        }
        return initOptions;
    }, [initOptions, debouncedQuery, isAsync]);

    const options = useMemo(() => {
        // we need to update existing options with created one if they have same id (option was simply updated)
        const createdOptionsToFilter = [];
        const updatedExistingOptions = existingOptions.map(option => {
            const updatedOption = createdOptions.find(item => item.value === option.value);
            if (updatedOption) {
                createdOptionsToFilter.push(updatedOption.value);
                return updatedOption;
            }
            return option;
        });
        const filteredCreatedOptions = createdOptions.filter(option => !createdOptionsToFilter.includes(option.value));
        const combinedOptions = [...filteredCreatedOptions, ...updatedExistingOptions];
        return combinedOptions;
    }, [createdOptions, existingOptions]);

    useEffect(() => {
        if (isAsync) {
            onInputChange(debouncedQuery);
        }
    }, [isAsync, debouncedQuery]);

    const wrapperClassName = cx("input-element comboBox-multi-checkbox", {
        isOpen: showOptions,
        disabled,
    });

    const inputClassName = cx("input-element", {
        "input-element__error": meta && meta.touched && meta.error,
    });
    const buttonClassName = cx("input-field", {
        "input-field__error": meta && meta.touched && meta.error,
        placeholder: placeholder,
    });

    const showErrorBlock = meta && meta.touched && !meta.active && meta.error;

    const openModal = () => {
        hideOptions();
        openAddressModal({
            customerId: customerId,
            customerObjectType: customerObjectType,
            staticBlockFormKey: STATIC_BLOCK_FORM_KEY,
            onAddressSave: handleAddressSave,
            onClose: closeModal,
            inputKey: input && input.name
        });
        if (onModalOpen && typeof onModalOpen === "function") {
            onModalOpen(true);
        }
    };
    const closeModal = () => {
        closeAddressModal();
        if (onModalOpen && typeof onModalOpen === "function") {
            onModalOpen(false);
        }
    };

    const getLabel = value => {
        if (isAsync) {
            const option = options.find(option => option.value === value.value);
            return option && option.label ? option.label.trim() : "";
        } else {
            const option = options.find(option => option.value === value);
            return option && option.label ? option.label.trim() : "";
        }
    };

    const toggleOptions = () => {
        if (!disabled) {
            if (showOptions) {
                input.onBlur();
            } else {
                input.onFocus();
            }
            if (onOpen && typeof onOpen === "function") {
                onOpen();
            }
            setShowOptions(prev => !prev);
        }
    };

    const hideOptions = e => {
        const fieldKey = input && input.name;
        let isFieldControllerParent = false;
        let isFieldControllerSubmit = false; // is field controller submit button clicked

        if (fieldKey) {
            const path = e ? e.path || (e.composedPath && e.composedPath()) : [];
            for (let i = 0; i < path.length; i++) {
                if (path[i].getAttribute && path[i].getAttribute("data-field-key")) {
                    isFieldControllerParent = path[i].getAttribute("data-field-key") === fieldKey;
                    // break;
                }
                if (path[i].getAttribute && path[i].getAttribute("data-field-controller-submit")) {
                    isFieldControllerSubmit = path[i].getAttribute("data-field-controller-submit") === "true";
                    // break;
                }
            }
        }

        // make sure that not submit button was clicked, if submit button was clicked => options should be hidden
        if (isFieldControllerParent && !isFieldControllerSubmit) {
            return;
        }

        if (showOptions) {
            input.onBlur();
            setShowOptions(false);
        }
        const { value, name } = input;
        if (submitOnBlur && meta.touched) {
            submitComboboxMulty(value, name);
        }
    };

    const renderCheckboxGroup = () => {
        return (
            <div className="optionsWrapper Address">
                <div className="buttonsWrapper">
                    <button type="button" className={cx("btn btn-add button-create")} onClick={openModal}>
                        <ReactSVG className="button-icon" src={`/data/svg/address-plus.svg`} />
                        <div className="button-text">{t("create")}</div>
                    </button>
                    {withSearch && (
                        <div className="input-element">
                            <input
                                ref={searchInputRef}
                                name={`${input.name}-SEARCH`}
                                placeholder={t("Search")}
                                className="input-field"
                                type="text"
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                        </div>
                    )}
                </div>
                <div className="options">
                    {options.map((option, index) => (
                        <div
                            className={cx("option-item", {
                                disabled: option.disabled || disabled,
                                "is-selected": option.value === input.value,
                            })}
                            key={index}
                            onClick={() => handleSelectOption(option)} // Handle option click
                        >
                            <span title={option.label} className="option-text">
                                {option.label}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const handleSelectOption = option => {
        input.onChange(option.value); // Update the selected value
        setShowOptions(false); // Close the dropdown
    };

    const renderSelectedValues = value => {
        if (!value) return placeholder; // Show placeholder if no value is selected
        return getLabel(value); // Get the label of the selected value
    };

    function handleAddressSave(address) {
        const existingOption = address && createdOptions.find(option => +option.value === +address.id);
        const newOption = { label: address.name, value: `${address.id}` };
        // options exist - update it existing option to the new one
        if (existingOption) {
            setCreatedOptions(prev => prev.map(option => (+option.value === +address.id ? newOption : option)));
        } else {
            // options does not exist - simply add new option
            setCreatedOptions(prev => [...prev, newOption]);
        }
        handleSelectOption(newOption);
        hideOptions();
    }

    useEffect(() => {
        if (showOptions) {
            const selectOptions = document.querySelector(".optionsWrapper");
            if (selectOptions.scrollIntoViewIfNeeded) {
                selectOptions.scrollIntoViewIfNeeded(false);
            } else {
                selectOptions.scrollIntoView(false);
            }
            if (searchInputRef && searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }
    }, [showOptions]);

    // useEffect(() => {
    //     if (onModalOpen && typeof onModalOpen === "function") {
    //         onModalOpen(isModalOpen);
    //     }
    // }, [isModalOpen]);

    return (
        <div className={inputClassName}>
            <div className="input-label">
                {label}
                {required && <span className="required-field">*</span>}
            </div>
            <ClickOutsideHolder className={wrapperClassName} onClickOutside={hideOptions}>
                <button className={buttonClassName} type="button" onClick={toggleOptions}>
                    {isLoading && (
                        <Loader
                            loaderContainerClass="container-loader container-loader-comboBox-multi-checkbox"
                            loaderClass="input-loader"
                        />
                    )}
                    <span name={input.name} className={input && input.value ? "input-value" : "placeholder"}>
                        {renderSelectedValues(input.value)}
                    </span>
                </button>
                {showOptions && renderCheckboxGroup()}
            </ClickOutsideHolder>
            {showErrorBlock && <ErrorBlock error={meta.error} className="m-r-10" />}
            {/* <AddressModal
                isOpen={isModalOpen}
                onClose={closeModal}
                customerId={customerId}
                customerObjectType={customerObjectType}
                onAddressSave={handleAddressSave}
                staticBlockFormKey={STATIC_BLOCK_FORM_KEY}
            /> */}
        </div>
    );
};

Address.propTypes = {
    options: PropTypes.array.isRequired,
    input: PropTypes.object.isRequired,
    disabled: PropTypes.bool,
    placeholder: PropTypes.string,
    error: PropTypes.object,
    meta: PropTypes.shape({
        error: PropTypes.string,
        label: PropTypes.string,
        touched: PropTypes.bool,
    }).isRequired,
};

export default withTranslation()(connect(null, mapDispatchToProps)(Address));
